import { useGetGroupsQuery, useUpdateGroupsMutation } from 'app/api/fleet';
import { Button, Table } from 'app/components';
import { LegacyRef, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './GroupEditorModal.module.scss';
import { ColorPicker } from 'app/components/ColorPicker';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { Field, Form, Formik } from 'formik';
import { columns as tableColumns } from './columns';
import { setSelectedRow } from 'app/utils/tableSlice';
import { GroupInfo, UpdateFleetGroupParams } from 'ais-facade-api/api';
import { isEmpty } from 'lodash';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';

const options = [
  '#FFFFFF',
  '#999999',
  '#000000',
  '#FFEE55',
  '#FEBC11',
  '#EF1010',
  '#73E916',
  '#16CFE9',
  '#FA00FF',
  '#3AB600',
  '#136AEC',
  '#C600B3',
];

interface Props {
  innerRef?: LegacyRef<HTMLDivElement>;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupEditorModal = ({ onClose, innerRef, onSuccess }: Props) => {
  const { t } = useTranslation();

  const { data, ...groupsRequest } = useGetGroupsQuery();
  const selectedRow = useAppSelector((state) => state.tableSlice.selectedRow);

  const dispatch = useAppDispatch();
  const { addAlert } = useAlertHook();

  const [updateGroups] = useUpdateGroupsMutation();

  const [groups, setGroups] = useState<GroupInfo[]>(data?.results || []);
  const [color, setColorValue] = useState('');

  const columns = useMemo<any>(() => tableColumns, []);

  useEffect(() => {
    if (data?.results) {
      setGroups(data?.results);
    }
  }, [data?.results]);

  useEffect(() => {
    if (selectedRow?.color) {
      setColorValue(selectedRow?.color);
    }
  }, [selectedRow]);

  const initialInputValue: any = useMemo(
    () => (groups?.reduce && groups?.reduce((prev, cur) => ({ ...prev, [(cur.key || 'new') as string]: cur }), {})) || {},
    [groups]
  );

  const handleSubmit = useCallback((values: any) => {
    const newGroups: UpdateFleetGroupParams[] = Object.values<UpdateFleetGroupParams>(values).filter((group: any) =>
      group?.key?.includes('new'),
    );

    const groupsToUpdate: UpdateFleetGroupParams[] = Object.values<UpdateFleetGroupParams>(values)?.reduce(
      (prev: UpdateFleetGroupParams[], cur) => {
        if (newGroups.includes(cur)) {
          return [...prev, { name: cur?.name, color: cur?.color, description: cur?.description }];
        } else {
          return [...prev, cur];
        }
      },
      [],
    );

    updateGroups(groupsToUpdate)
      .unwrap()
      .then(() => {
        onSuccess();
        // TODO доработать алерты, чтобы уметь отображать разные сообщения
        addAlert({
          data: {
            title: 'Success',
            status: 200,
            detail: 'Success',
            messages: [{ description: t('ChangesSaved'), type: 'information' }],
          },
        });
        dispatch(setSelectedRow(null));

        groupsRequest.refetch();
        onClose();
      })
      .catch((error: Error) => {
        addAlert(error);
      });
  }, []);

  const handleAddRowClick = useCallback(() => {
    const newGroupName = t('NewGroupName');

    const newGroupsNumbers: number[] = groups?.reduce((prev: number[], cur) => {
      if (cur.key?.includes('new')) {
        const num = cur.key.split(`new-`)[1];
        return [...prev, Number(num)];
      }
      return [...prev];
    }, []);

    const newGroupNumber = newGroupsNumbers?.length > 0 ? Math.max(...newGroupsNumbers) + 1 : 1;
    const newRow = {
      key: `new-${newGroupNumber}`,
      name: `${newGroupName} ${newGroupNumber}`,
      color: options[Math.floor(Math.random() * (options.length - 1))],
      description: '',
    };

    setGroups((prev) => [...prev, newRow]);
    dispatch(setSelectedRow(null));
  }, [options, groups]);

  const handleDeleteGroup = useCallback(() => {
    if (selectedRow?.key) {
      setGroups((prev) => prev.filter((group) => group.key !== selectedRow.key));
      dispatch(setSelectedRow(null));
    }
  }, [selectedRow]);

  return (
    <div className={styles.root}>
      <div className={styles.dialog_modal} ref={innerRef}>
        <div className={styles.panel_header}>
          <p className={styles.heading}>{t('Groups')}</p>
          <div
            onClick={() => {
              dispatch(setSelectedRow(null));
              onClose();
            }}
            className={styles.close_button}
          >
            <Icon name="close-16" size="16" />
          </div>
        </div>
        <div className={styles.form_container}>
          <>
            <div className={styles.buttons}>
              <Button variant="general" label={t('ButtonLabelAdd')} type="button" onClick={handleAddRowClick} />
              <Button
                variant="general"
                label={t('ButtonLabelDelete')}
                type="button"
                disabled={isEmpty(selectedRow)}
                onClick={handleDeleteGroup}
              />
            </div>
            {groupsRequest.isLoading && <span className={styles.loading}> {t('SearchingText')}</span>}

            {groups && groups?.length > 0 ? (
              <div className={styles.table_container}>
                <Table withSelect data={groups} columns={columns} />
              </div>
            ) : null}
          </>

          <div className={styles.group_edit}>
            {`${t('GroupParametrs')}:`}
            <Formik
              initialValues={initialInputValue}
              enableReinitialize={true}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values, { resetForm }) => {
                handleSubmit(values);
                resetForm();
              }}
            >
              {({ values, setFieldValue, resetForm, errors }) => {
                const selectedColor = values[`${selectedRow?.key}` as any]?.color || color;

                return (
                  <Form
                    className={styles.form}
                    onBlur={(e) => {
                      if (e.relatedTarget?.['id']?.includes(selectedRow?.key)) {
                        e.stopPropagation();
                      } else {
                        setGroups(() => {
                          const a = groups.reduce((prev: GroupInfo[], cur) => {
                            if (cur?.key === selectedRow?.key) {
                              return [
                                ...prev,
                                {
                                  ...cur,
                                  color: values[`${selectedRow?.key}` as any]?.color,
                                  name: values[`${selectedRow?.key}` as any]?.name,
                                  description: values[`${selectedRow?.key}` as any]?.description,
                                },
                              ];
                            }
                            return [...prev, cur];
                          }, []);
                          return a;
                        });
                        dispatch(setSelectedRow(null));
                      }
                    }}
                  >
                    <div className={styles.fields}>
                      <label className={styles.group_param_label}>
                        {`${t('Color')}:`}
                        <ColorPicker
                          disabled={isEmpty(selectedRow)}
                          selected={selectedColor}
                          id={`${selectedRow?.key}[color]`}
                          onChange={(value) => {
                            setColorValue(value);
                            setFieldValue(`${selectedRow?.key}[color]`, value);

                            setGroups(() => {
                              const a = groups.reduce((prev: GroupInfo[], cur) => {
                                if (cur?.key === selectedRow?.key) {
                                  return [...prev, { ...cur, color: value }];
                                }
                                return [...prev, cur];
                              }, []);
                              return a;
                            });
                            dispatch(setSelectedRow(null));
                          }}
                          options={options}
                        />
                      </label>

                      <label className={styles.group_param_label}>
                        {`${t('Name')}:`}
                        <>
                          <Field
                            disabled={isEmpty(selectedRow)}
                            id={`${selectedRow?.key}[name]`}
                            className={styles.modal_input}
                            name={`${selectedRow?.key}[name]`}
                            placeholder={t('FieldPlaceholderGroupName')}
                            type="string"
                            validate={(value: string) => {
                              let error;

                              if (value?.length === 0) {
                                error = t('IncorrectGroupNameLenghtError');
                              } else if (value && !(value?.length >= 1 && value?.length <= 64)) {
                                error = t('IncorrectGroupNameLenghtError');
                              } else {
                                error = null;
                              }
                              return error;
                            }}
                          />
                        </>
                      </label>

                      <label className={styles.group_param_label}>
                        {`${t('Description')}:`}
                        <Field
                          disabled={isEmpty(selectedRow)}
                          id={`${selectedRow?.key}[description]`}
                          className={styles.modal_input}
                          name={`${selectedRow?.key}[description]`}
                          placeholder={t('FieldPlaceholderGroupDescription')}
                          type="string"
                        />
                      </label>

                      {!isEmpty(errors[selectedRow?.key]) &&
                        selectedRow &&
                        selectedRow?.key &&
                        (errors as any)[selectedRow?.key]?.name && (
                          <p className={styles.field_error}>{(errors as any)[selectedRow?.key]?.name}</p>
                        )}
                    </div>

                    <div className={styles.buttons}>
                      <Button variant="general" label={t('ButtonLabelSave')} type="submit" disabled={!isEmpty(errors)} />
                      <Button
                        variant="general"
                        label={t('ButtonLabelDiscard')}
                        type="reset"
                        onClick={() => {
                          resetForm();
                          onClose();
                          dispatch(setSelectedRow(null));
                        }}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
